import React, { useLayoutEffect, useRef } from 'react';
import { LockClosedIcon } from '@heroicons/react/16/solid';
import { classNames } from '../utils/classNames';

// The navigation of a module with few pages (the BI section): a slim column of icons with a short
// label under each, under the top bar of the app, so the page keeps most of its width for the data.
// The items come in groups; a thin line separates the groups. The active item has a blue-50 ground
// and blue-700 text. A disabled item is gray, shows a lock, and its title says why.
// Below the sm width the rail becomes a row under the top bar that scrolls sideways. The row scrolls the active item
// into view when it opens and when the active item changes, so a phone always shows the page that is open.
// The rail knows nothing about the pages: the caller gives the items and handles the click (for
// example with the router of the app).

export type IconRailItem = {
	id: string,
	label: string,
	// A 24 px outline icon (heroicons).
	icon: React.ComponentType<React.SVGProps<SVGSVGElement>>,
	active?: boolean,
	disabled?: boolean,
	// The tooltip; for a disabled item, the reason.
	title?: string,
	onSelect?: () => void,
};

type Props = {
	// The accessible name of the navigation ("BI reports").
	label: string,
	groups: IconRailItem[][],
};

const Item: React.FunctionComponent<{ item: IconRailItem, row?: boolean }> = ({ item, row = false }) => {
	const Icon = item.icon;
	const active = item.active === true;
	const disabled = item.disabled === true;
	return <button type="button" title={item.title ?? item.label} aria-current={active ? 'page' : undefined} disabled={disabled}
		onClick={() => { if (!active && item.onSelect !== undefined) item.onSelect(); }}
		className={classNames(
			'flex shrink-0 flex-col items-center gap-y-0.5 rounded-lg text-center focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600',
			row ? 'px-3 py-1.5' : 'mx-1 py-2',
			active ? 'bg-blue-50' : (disabled ? 'cursor-not-allowed' : 'hover:bg-gray-50'),
		)}>
		<span className="relative inline-flex">
			<Icon aria-hidden="true" className={classNames('h-6 w-6', active ? 'text-blue-600' : (disabled ? 'text-gray-300' : 'text-gray-500'))} />
			{disabled && <LockClosedIcon aria-hidden="true" className="absolute -bottom-0.5 -right-1.5 h-3 w-3 text-gray-400" />}
		</span>
		<span className={classNames('text-xs leading-4', row ? 'whitespace-nowrap' : '',
			active ? 'font-semibold text-blue-700' : (disabled ? 'font-medium text-gray-400' : 'font-medium text-gray-600'))}>
			{item.label}
		</span>
	</button>;
};

const IconRail: React.FunctionComponent<Props> = ({ label, groups }) => {
	const row = useRef<HTMLDivElement>(null);
	const activeId = groups.flat().find(item => item.active === true)?.id;
	// Only the row scrolls (scrollIntoView would also scroll the page). The active item comes to the middle of the
	// row when it can; the browser keeps scrollLeft inside the row.
	useLayoutEffect(() => {
		const element = row.current;
		const item = element?.querySelector<HTMLElement>('[aria-current="page"]');
		if (element === null || element === undefined || item === null || item === undefined) return;
		if (item.offsetLeft >= element.scrollLeft && item.offsetLeft + item.offsetWidth <= element.scrollLeft + element.clientWidth) return;
		element.scrollLeft = item.offsetLeft - (element.clientWidth - item.offsetWidth) / 2;
	}, [activeId]);
	return <nav aria-label={label}>
		<div className="hidden h-full w-24 flex-none flex-col border-r border-gray-200 bg-white py-3 sm:flex">
			{groups.map((group, g) => <React.Fragment key={g}>
				{g > 0 && <div aria-hidden="true" className="mx-4 my-2 h-px bg-gray-200" />}
				<div className="flex flex-col gap-y-1">{group.map(item => <Item key={item.id} item={item} />)}</div>
			</React.Fragment>)}
		</div>
		<div ref={row} className="relative flex gap-x-1 overflow-x-auto border-b border-gray-200 bg-white px-2 py-1.5 sm:hidden">
			{groups.flat().map(item => <Item key={item.id} item={item} row />)}
		</div>
	</nav>;
};

export default IconRail;
